import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AppLayout } from '../components/AppLayout'
import { meApi, ordersApi } from '../api/endpoints'
import type { Order, PaymentTerms } from '../api/types'

/**
 * /notifications — derived feed mirroring the mobile inbox. Source data
 * is /me + /orders/invoices; entries are synthesized for overdue invoices,
 * upcoming due dates, KYC pending, and recent paid orders. Read-state
 * persists in localStorage. When server-side notification events ship
 * (FCM history persisted into Firestore), this swaps to /me/notifications.
 */

const READ_KEY = 'aska.notifications.read'

interface Notification {
  id: string
  kind: 'overdue' | 'due_soon' | 'order' | 'kyc'
  title: string
  body: string
  createdAt: number
  href?: string
  level: 'urgent' | 'info' | 'success'
}

function getReadIds(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_KEY)
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set()
  } catch { return new Set() }
}

function markRead(ids: string[]) {
  try {
    const cur = getReadIds()
    for (const id of ids) cur.add(id)
    localStorage.setItem(READ_KEY, JSON.stringify(Array.from(cur)))
  } catch { /* ignore */ }
}

function deriveNotifications(terms: PaymentTerms | null, orders: Order[]): Notification[] {
  const out: Notification[] = []
  const now = Date.now()

  if (terms && terms.days > 0) {
    for (const o of orders) {
      if (Number(o.balance) <= 0) continue
      const placed = o.orderDate ? new Date(o.orderDate).getTime() : 0
      if (!placed) continue
      const dueAt = placed + terms.days * 24 * 60 * 60 * 1000
      const daysOff = Math.round((dueAt - now) / (24 * 60 * 60 * 1000))
      if (daysOff < 0) {
        out.push({
          id: `overdue-${o.orderId}`,
          kind: 'overdue',
          title: `Order #${o.orderId} is ${Math.abs(daysOff)} days overdue`,
          body: `$${Math.round(Number(o.balance)).toLocaleString()} balance — settle to keep your account in good standing.`,
          createdAt: dueAt,
          href: `/orders/${o.orderId}`,
          level: 'urgent',
        })
      } else if (daysOff <= 7) {
        out.push({
          id: `due-soon-${o.orderId}`,
          kind: 'due_soon',
          title: `Order #${o.orderId} due in ${daysOff} days`,
          body: `$${Math.round(Number(o.balance)).toLocaleString()} balance — payment window closing soon.`,
          createdAt: dueAt,
          href: `/orders/${o.orderId}`,
          level: 'info',
        })
      }
    }
  }

  const recent = [...orders]
    .filter((o) => Number(o.balance) === 0)
    .sort((a, b) => {
      const da = a.orderDate ? new Date(a.orderDate).getTime() : 0
      const db = b.orderDate ? new Date(b.orderDate).getTime() : 0
      return db - da
    })
    .slice(0, 10)
  for (const o of recent) {
    const ts = o.orderDate ? new Date(o.orderDate).getTime() : Date.now()
    out.push({
      id: `paid-${o.orderId}`,
      kind: 'order',
      title: `Order #${o.orderId} paid`,
      body: `$${Math.round(Number(o.amount)).toLocaleString()} cleared. Thank you.`,
      createdAt: ts,
      href: `/orders/${o.orderId}`,
      level: 'success',
    })
  }

  out.sort((a, b) => b.createdAt - a.createdAt)
  return out
}

export function useUnreadCount(): number {
  const [count, setCount] = useState(0)
  useEffect(() => {
    let cancelled = false
    Promise.all([meApi.get(), ordersApi.invoices()])
      .then(([me, inv]) => {
        if (cancelled) return
        const list = deriveNotifications(me.terms, inv.orders || [])
        const read = getReadIds()
        const unread = list.filter((n) =>
          (n.level === 'urgent' || n.kind === 'due_soon') && !read.has(n.id))
        setCount(unread.length)
      })
      .catch(() => { /* silent */ })
    return () => { cancelled = true }
  }, [])
  return count
}

export function Notifications() {
  const navigate = useNavigate()
  const [items, setItems] = useState<Notification[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const readIds = useMemo(() => getReadIds(), [])

  useEffect(() => {
    let cancelled = false
    Promise.all([meApi.get(), ordersApi.invoices()])
      .then(([me, inv]) => {
        if (cancelled) return
        const list = deriveNotifications(me.terms, inv.orders || [])
        setItems(list)
        markRead(list.map((n) => n.id))
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Could not load notifications') })
    return () => { cancelled = true }
  }, [])

  return (
    <AppLayout>
      <div className="mx-auto max-w-2xl px-6 py-8 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight">Notifications</h1>
          <p className="mt-1 text-sm text-muted">Important updates on your account and orders.</p>
        </div>

        {!items && !error && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-md skeleton" />)}
          </div>
        )}
        {error && (
          <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-4">
            {error}
          </div>
        )}
        {items && items.length === 0 && (
          <div className="card text-center py-10">
            <p className="text-base font-semibold text-ink">All quiet</p>
            <p className="mt-1 text-sm text-muted">No new updates right now.</p>
          </div>
        )}
        {items && items.length > 0 && (
          <ul className="space-y-2">
            {items.map((n, i) => (
              <motion.li
                key={n.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: Math.min(i * 0.03, 0.3) }}
              >
                <button
                  type="button"
                  onClick={() => n.href && navigate(n.href)}
                  className="flex w-full items-start gap-3 card text-left hover:bg-page transition-colors"
                >
                  <NotificationIcon level={n.level} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-semibold leading-tight ${
                      n.level === 'urgent' ? 'text-danger'
                      : n.level === 'success' ? 'text-success'
                      : 'text-ink'}`}>
                      {n.title}
                    </p>
                    <p className="mt-0.5 text-xs text-muted leading-snug">{n.body}</p>
                    <p className="mt-1 text-[10.5px] text-soft">
                      {new Date(n.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  {!readIds.has(n.id) && (
                    <span
                      className={`mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full ${
                        n.level === 'urgent' ? 'bg-danger' : 'bg-primary'
                      }`}
                      aria-label="Unread"
                    />
                  )}
                </button>
              </motion.li>
            ))}
          </ul>
        )}
      </div>
    </AppLayout>
  )
}

function NotificationIcon({ level }: { level: Notification['level'] }) {
  const cls = level === 'urgent'
    ? 'bg-danger/10 text-danger'
    : level === 'success'
      ? 'bg-success/10 text-success'
      : 'bg-primary/10 text-primary'
  const path = level === 'urgent' ? (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </>
  ) : level === 'success' ? (
    <polyline points="20 6 9 17 4 12" />
  ) : (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </>
  )
  return (
    <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${cls}`}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        {path}
      </svg>
    </span>
  )
}
